import { useEffect, useRef, useState, useMemo } from "react";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

const IconClock = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display: "inline-block", verticalAlign: "middle" }}>
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <circle cx="12" cy="12" r="7" fill="none" stroke="#fff" strokeWidth="1.6" />
    <path d="M12 7v5l3.5 2.1" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


const DEFAULT_MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

// Same 15-minute slots as the event form time picker (EFTimePill).
const QUARTER_HOUR_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = String(Math.floor(i / 4)).padStart(2, "0");
  const m = String((i % 4) * 15).padStart(2, "0");
  return `${h}:${m}`;
});

const OrganizerTimePillSelect = ({
  value = "",
  onChange,
  placeholder = "--:--",
  disabled = false,
  className = "",
  triggerClassName = "zoco-control is-select",
  showClockIcon = true,
  style = {},
  triggerStyle = {},
  ariaLabel,
  minutes = DEFAULT_MINUTES,
  singleList = false,
  quarterHours = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const [currentHour, currentMinute] = useMemo(() => {
    const raw = value || "";
    const parts = raw.split(":");
    return [parts[0] || "", parts[1] || ""];
  }, [value]);

  const singleListOptions = useMemo(() => {
    const opts = [];
    for (let h = 6; h < 24; h++) {
      opts.push(`${String(h).padStart(2, "0")}:00`);
      opts.push(`${String(h).padStart(2, "0")}:30`);
    }
    for (let h = 0; h < 6; h++) {
      opts.push(`${String(h).padStart(2, "0")}:00`);
      opts.push(`${String(h).padStart(2, "0")}:30`);
    }
    return opts;
  }, []);

  const listOptions = quarterHours ? QUARTER_HOUR_OPTIONS : singleListOptions;

  const formatSingleListLabel = (val) => {
    if (quarterHours) return val;
    const parts = val.split(":");
    const h = parseInt(parts[0], 10);
    return `${h}:${parts[1]} hs`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (containerRef.current) {
          const activeButtons = containerRef.current.querySelectorAll(".zoco-time-option.is-active");
          activeButtons.forEach((btn) => {
            btn.scrollIntoView({ block: "center", behavior: "auto" });
          });
        }
      }, 50);
    }
  }, [open]);

  const handleSelectHour = (h) => {
    const nextMinute = currentMinute || "00";
    if (typeof onChange === "function") {
      onChange(`${h}:${nextMinute}`);
    }
  };

  const handleSelectMinute = (m) => {
    const nextHour = currentHour || "12";
    if (typeof onChange === "function") {
      onChange(`${nextHour}:${m}`);
    }
  };

  const isPill = triggerClassName.includes("ef-date-pill-trigger");
  const displayLabel = value
    ? (isPill ? `${value} hs` : value)
    : placeholder;

  return (
    <div
      className={`zoco-time-picker-wrap ${className}`}
      ref={containerRef}
      style={style}
    >
      <button
        type="button"
        className={`${triggerClassName} ${disabled ? "is-disabled is-muted" : ""} ${value ? "has-value" : ""}`}
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-label={ariaLabel}
        style={triggerStyle}
      >
        <span className={isPill ? "ef-date-pill-label" : ""}>
          {displayLabel}
        </span>
        {showClockIcon && (
          <span className={isPill ? "icon" : "zoco-clock"}>
            <IconClock size={16} />
          </span>
        )}
      </button>
      {!disabled && open && (
        <div className="zoco-time-pill-panel">
          {singleList || quarterHours ? (
            <div className="zoco-time-scroll">
              {listOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`zoco-time-option ${value === opt ? "is-active" : ""}`}
                  onClick={() => {
                    if (typeof onChange === "function") {
                      onChange(opt);
                    }
                    setOpen(false);
                  }}
                >
                  {formatSingleListLabel(opt)}
                </button>
              ))}
            </div>
          ) : (
            <div className="zoco-time-columns">
              <div className="zoco-time-column">
                <div className="zoco-time-scroll">
                  {HOURS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      className={`zoco-time-option ${currentHour === h ? "is-active" : ""}`}
                      onClick={() => handleSelectHour(h)}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>
              <div className="zoco-time-column">
                <div className="zoco-time-scroll">
                  {minutes.map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`zoco-time-option ${currentMinute === m ? "is-active" : ""}`}
                      onClick={() => handleSelectMinute(m)}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizerTimePillSelect;
